import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

interface Domain {
  id: string;
  name: string;
  description?: string;
  color?: string;
  domain_role?: string;
}

interface User {
  id: string;
  username: string;
  name: string;
  email?: string;
  role: string;
  is_active: number;
  permissions: string[];
  domains: Domain[];
  created_at?: string;
  last_login_at?: string;
}

interface UserManagementProps {
  currentUser: {
    id: string;
    role: string;
  };
}

const UserManagement: React.FC<UserManagementProps> = ({ currentUser }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDomainModal, setShowDomainModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    name: '',
    email: '',
    role: 'viewer'
  });
  const [domainAssignment, setDomainAssignment] = useState({
    domainId: '',
    role: 'viewer'
  });

  useEffect(() => {
    loadUsers();
    loadDomains();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/users`);
      setUsers((response.data as any).users || []);
    } catch (error) {
      console.error('Error loading users:', error);
      alert('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadDomains = async () => {
    try {
      const response = await axios.get(`${API_BASE}/domains`);
      setDomains((response.data as any).domains || []);
    } catch (error) {
      console.error('Error loading domains:', error);
    }
  };

  const createUser = async () => {
    if (!formData.username || !formData.password || !formData.name) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API_BASE}/users`, formData);
      setShowCreateModal(false);
      setFormData({ username: '', password: '', name: '', email: '', role: 'viewer' });
      await loadUsers();
      alert('User created successfully');
    } catch (error: any) {
      console.error('Error creating user:', error);
      alert(error.response?.data?.error || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async () => {
    if (!selectedUser) return;

    try {
      setLoading(true);
      await axios.put(`${API_BASE}/users/${selectedUser.id}`, {
        name: formData.name,
        email: formData.email,
        role: formData.role
      });
      setShowEditModal(false);
      setSelectedUser(null);
      await loadUsers();
      alert('User updated successfully');
    } catch (error: any) {
      console.error('Error updating user:', error);
      alert(error.response?.data?.error || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) return;

    try {
      setLoading(true);
      await axios.delete(`${API_BASE}/users/${userId}`);
      await loadUsers();
      alert('User deactivated successfully');
    } catch (error: any) {
      console.error('Error deleting user:', error);
      alert(error.response?.data?.error || 'Failed to deactivate user');
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (userId: string) => {
    const newPassword = prompt('Enter new password (min 6 characters):');
    if (!newPassword) return;
    if (newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API_BASE}/users/${userId}/reset-password`, { newPassword });
      alert('Password reset successfully');
    } catch (error: any) {
      console.error('Error resetting password:', error);
      alert(error.response?.data?.error || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (user: User) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      password: '',
      name: user.name,
      email: user.email || '',
      role: user.role
    });
    setShowEditModal(true);
  };

  const openDomainModal = (user: User) => {
    setSelectedUser(user);
    setDomainAssignment({ domainId: '', role: 'viewer' });
    setShowDomainModal(true);
  };

  const assignDomain = async () => {
    if (!selectedUser || !domainAssignment.domainId) return;

    try {
      setLoading(true);
      await axios.post(`${API_BASE}/users/${selectedUser.id}/domains`, domainAssignment);
      setShowDomainModal(false);
      await loadUsers();
      alert('Domain assigned successfully');
    } catch (error: any) {
      console.error('Error assigning domain:', error);
      alert(error.response?.data?.error || 'Failed to assign domain');
    } finally {
      setLoading(false);
    }
  };

  const removeDomain = async (userId: string, domainId: string) => {
    if (!window.confirm('Remove user from this domain?')) return;

    try {
      setLoading(true);
      await axios.delete(`${API_BASE}/users/${userId}/domains/${domainId}`);
      await loadUsers();
    } catch (error: any) {
      console.error('Error removing domain:', error);
      alert(error.response?.data?.error || 'Failed to remove domain');
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'super_admin': return 'badge badge-danger';
      case 'admin': return 'badge badge-warning';
      case 'editor': return 'badge badge-info';
      default: return 'badge badge-secondary';
    }
  };

  return (
    <div className="user-management">
      <div className="management-header">
        <h2>User Management</h2>
        <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
          Create User
        </button>
      </div>

      {loading && <div className="loading">Loading...</div>}

      <table className="management-table">
        <thead>
          <tr>
            <th>Username</th>
            <th>Name</th>
            <th>Email</th>
            <th>Role</th>
            <th>Domains</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map(user => (
            <tr key={user.id} className={user.is_active ? '' : 'inactive-row'}>
              <td>{user.username}</td>
              <td>{user.name}</td>
              <td>{user.email || '-'}</td>
              <td>
                <span className={getRoleBadgeClass(user.role)}>
                  {user.role}
                </span>
              </td>
              <td>
                <div className="domain-badges">
                  {user.domains.map(domain => (
                    <span
                      key={domain.id}
                      className="domain-badge"
                      style={{ backgroundColor: domain.color || '#6366f1' }}
                      title={`${domain.name} (${domain.domain_role || 'viewer'})`}
                    >
                      {domain.name}
                      {user.id !== currentUser.id && (
                        <button
                          className="domain-remove"
                          onClick={() => removeDomain(user.id, domain.id)}
                          title="Remove from domain"
                        >
                          x
                        </button>
                      )}
                    </span>
                  ))}
                  {user.role !== 'super_admin' && (
                    <button
                      className="domain-add"
                      onClick={() => openDomainModal(user)}
                      title="Add to domain"
                    >
                      +
                    </button>
                  )}
                </div>
              </td>
              <td>
                <span className={user.is_active ? 'status-active' : 'status-inactive'}>
                  {user.is_active ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td>
                <div className="action-buttons">
                  <button
                    className="btn-small btn-secondary"
                    onClick={() => openEditModal(user)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn-small btn-warning"
                    onClick={() => resetPassword(user.id)}
                  >
                    Reset PW
                  </button>
                  {user.id !== currentUser.id && (
                    <button
                      className="btn-small btn-danger"
                      onClick={() => deleteUser(user.id)}
                    >
                      Deactivate
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Create New User</h3>
            <div className="form-group">
              <label>Username *</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder="Enter username"
              />
            </div>
            <div className="form-group">
              <label>Password *</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Enter password (min 6 chars)"
              />
            </div>
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter full name"
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter email (optional)"
              />
            </div>
            <div className="form-group">
              <label>Role</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              >
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={createUser} disabled={loading}>
                {loading ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Edit User: {selectedUser.username}</h3>
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter full name"
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter email (optional)"
              />
            </div>
            <div className="form-group">
              <label>Role</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              >
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowEditModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={updateUser} disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Domain Modal */}
      {showDomainModal && selectedUser && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Assign Domain to {selectedUser.name}</h3>
            <div className="form-group">
              <label>Domain</label>
              <select
                value={domainAssignment.domainId}
                onChange={(e) => setDomainAssignment({ ...domainAssignment, domainId: e.target.value })}
              >
                <option value="">Select a domain...</option>
                {domains
                  .filter(d => !selectedUser.domains.find(ud => ud.id === d.id))
                  .map(domain => (
                    <option key={domain.id} value={domain.id}>
                      {domain.name}
                    </option>
                  ))}
              </select>
            </div>
            <div className="form-group">
              <label>Domain Role</label>
              <select
                value={domainAssignment.role}
                onChange={(e) => setDomainAssignment({ ...domainAssignment, role: e.target.value })}
              >
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowDomainModal(false)}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={assignDomain}
                disabled={loading || !domainAssignment.domainId}
              >
                {loading ? 'Assigning...' : 'Assign Domain'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
